import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { loadRawEntityDataByIDs } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import type { ParticipationWorkshop } from '../entities/ParticipationWorkshop.js';
import { Product } from '../entities/products.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import type { Workshop } from '../entities/workshop.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

// Mail template for Nutzer-Sperrung
defineMailTemplate(MailTemplateType.SPERRUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Sperrung durch Veranstalter',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir haben Sie soeben von unseren Veranstaltungen abgemeldet, da Sie in einer oder mehreren Veranstaltungen unentschuldigt gefehlt haben. Ihr Fehlen hat zur Folge, dass Sie im laufenden Semester gesperrt wurden und Sie gleichzeitig alle Pl&auml;tze in anderen Veranstaltungen verlieren. Im n&auml;chsten Semester k&ouml;nnen Sie sich wieder f&uuml;r Veranstaltungen anmelden.</p>
<p>Hinweis: Falls uns ein Fehler unterlaufen sein sollte, bitten wir Sie, sich mit uns <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a> in Verbindung zu setzen.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>You just have been deregistered from our courses because you have been absent from one or more courses without excuse. Your absence has resulted in you bing blocked for the current semester, and at the same time you will los all seats in other classes. You will be able to register for events again next semester.</p>
<p>If we have made a mistake, please contact us by <a href="mailto:contact@anmeldung.uni-frankfurt.de">mail</a>.</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.SPERRUNG),
  attachments: [],
  displayName: 'Sperrung',
  editorHint: () => institutionMailHint(MailTemplateType.SPERRUNG),
});

export async function notifyNuzterSperrung(participationIDs: string[]) {
  if (participationIDs.length === 0) return;

  const participationsData = await loadRawEntityDataByIDs(Participation, participationIDs, {
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitinglistPlace: 1,
  });

  if (participationsData.length === 0) {
    logger.error({ participationsData }, 'no participations found');
    throw new Error('no participations found');
  }

  const products = await loadRawEntityDataByIDs(
    Product,
    Array.from(new Set(participationsData.map(p => p.productID))),
    {
      _id: 1,
      _class: 1,
      name: 1,
      institutionID: 1,
      topic: 1,
      language: 1,
      appointments: {
        start: 1,
        end: 1,
        name: 1,
        location: 1,
        meetingLink: { link: 1 },
        slots: { _id: 1, start: 1, end: 1 },
      },
    },
  );

  const userIDs = Array.from(new Set(participationsData.map(p => p.userID)));
  if (userIDs.length > 1) {
    logger.error({ participationsData }, 'participations arent from the same user');
    throw new Error('participations arent from the same user');
  }

  const users = await loadRawEntityDataByIDs(User, userIDs, {
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
  });

  const participations = participationsData.map(p => {
    const product = products.find(pr => pr._id === p.productID);
    const user = users.find(u => u._id === p.userID);
    if (!product) throw new Error('no product found for participation with ID ' + p._id);
    if (!user) throw new Error('no user found for participation with ID ' + p._id);
    return {
      ...p,
      product,
      user,
    };
  });

  const institutionID = participations.at(0)?.product?.institutionID;
  if (!institutionID) throw new Error('Couldnt find institution for participation with ID ' + participationIDs[0]);
  for (const p of participations) {
    if (p.product.institutionID !== institutionID) {
      throw new Error(
        'we can only group participations from the same institution; ' +
          JSON.stringify({ participationIDs, institutionID, institutionID2: p.product.institutionID }),
      );
    }
  }

  const user = participations.at(0)?.user;
  if (!user) throw new Error('no user is set');

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  // Get institution with color definitions
  const institution = await getInstitutionByID(institutionID)({
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.SPERRUNG,
  )(InstitutionMailTemplate.allFields);

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.TITLE]: user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: user?.firstName,
      [MailTemplateVars.LAST_NAME]: user?.lastName,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML(
        participations.map(participation => ({
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: (participation as ParticipationWorkshop).waitingListPlace,
          appointments:
            (
              participation.product as any as NarrowedEntityData<
                Workshop,
                { appointments: { name: 1; location: 1; start: 1; end: 1 } }
              >
            )?.appointments || [],
          englishVersion: false,
        })),
      ),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML(
        participations.map(participation => ({
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: (participation as ParticipationWorkshop).waitingListPlace,
          appointments:
            (
              participation.product as any as NarrowedEntityData<
                Workshop,
                { appointments: { name: 1; location: 1; start: 1; end: 1 } }
              >
            )?.appointments || [],
          englishVersion: true,
        })),
      ),
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.SPERRUNG,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
